import { Field, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, User, Building2 } from "lucide-react";
import CountryInput from "../../Common/Inputs/CountryInput/CountryInput";
import FieldError from "../../Common/Form/FieldError";
const AddressForm = ({
    title,
    addresses,
    type,
    disabled,
    errors, // Ajouter les erreurs ici
    useCustomerInfo = false,
    therapist,
    customer,
    shippingType,
    hidePersonalInfo = false, // Nouvelle prop pour masquer les infos personnelles
    showSectionTitle = true, // Nouvelle prop pour afficher/masquer les titres de section
    showCompanyField = false, // Nouvelle prop pour afficher le champ company dans shipping
}) => {


    const formik = useFormikContext()
    const selectedAddressId = type === 'shipping' ? formik.values.shippingAddressId : formik.values.billingAddressId
    const [isTransitioning, setIsTransitioning] = useState(false)


    const handleAddressChange = (type) => (e) => {
        const field = type === 'shipping' ? 'shippingAddressId' : 'billingAddressId'

        // Capturer la valeur avant le setTimeout pour éviter les problèmes d'accès à e.target
        const value = e.target.value

        // Déclencher l'animation de transition
        setIsTransitioning(true)

        // Petit délai pour l'animation
        setTimeout(() => {
            if (value === "new") {
                formik.setFieldValue(field, null)
                resetFormAddress(type)
                // Reset form error
                formik.setErrors({})
            } else {
                const parsedId = parseInt(value)
                formik.setFieldValue(field, parsedId)
                updateFormAddress(type, parsedId)
            }
            setIsTransitioning(false)
        }, 150)
    }


    const updateFormAddress = (type, addressId) => {
        const address = addresses?.find(address => address.id === parseInt(addressId))
        const firstName = type === 'shipping' && shippingType === 'customer' ? customer?.firstname : therapist?.firstname
        const lastName = type === 'shipping' && shippingType === 'customer' ? customer?.lastname : therapist?.lastname

        if (!address) {
            return
        }

        formik.setFieldValue(`${type}.address_1`, address.street || "")
        formik.setFieldValue(`${type}.city`, address.city || "")
        formik.setFieldValue(`${type}.postcode`, address.postcode || "")
        formik.setFieldValue(`${type}.state`, address.state === null ? "" : (address.state || ""))
        formik.setFieldValue(`${type}.country`, address.isoCountry || "")
        formik.setFieldValue(`${type}.first_name`, firstName || "")
        formik.setFieldValue(`${type}.last_name`, lastName || "")

        // Pré-remplir le champ company pour les adresses de facturation OU shipping si showCompanyField
        if (type === 'billing' || (type === 'shipping' && showCompanyField)) {
            formik.setFieldValue(`${type}.company`, address.company || "")
        }
    }

    const resetFormAddress = (type) => {
        const firstName = type === 'shipping' && shippingType === 'customer' ? customer?.firstname : therapist?.firstname
        const lastName = type === 'shipping' && shippingType === 'customer' ? customer?.lastname : therapist?.lastname

        formik.setFieldValue(`${type}.address_1`, "")
        formik.setFieldValue(`${type}.city`, "")
        formik.setFieldValue(`${type}.postcode`, "")
        formik.setFieldValue(`${type}.state`, "")
        formik.setFieldValue(`${type}.country`, "")
        formik.setFieldValue(`${type}.first_name`, firstName || "")
        formik.setFieldValue(`${type}.last_name`, lastName || "")

        // Réinitialiser le champ company pour les adresses de facturation ET shipping
        formik.setFieldValue(`${type}.company`, "")
    }

    const handleFieldChange = (field) => (e) => {
        const value = e.target.value;

        // Mettre à jour la valeur du champ
        formik.setFieldValue(field, value)

        // Validation en temps réel si le champ a déjà été touché
        if (formik.touched[field]) {
            formik.validateField(field)
        }
    }

    const handleFieldBlur = (field) => (e) => {
        formik.setFieldTouched(field, true)
        formik.validateField(field)
    }


    useEffect(() => {
        // Seulement déclencher updateFormAddress si un ID d'adresse valide est sélectionné
        if (type === 'shipping' && formik.values.shippingAddressId && formik.values.shippingAddressId !== null) {
            updateFormAddress(type, formik.values.shippingAddressId)
        }
        if (type === 'billing' && formik.values.billingAddressId && formik.values.billingAddressId !== null) {
            updateFormAddress(type, formik.values.billingAddressId)
        }
    }, [formik.values.shippingAddressId, formik.values.billingAddressId, type, addresses])

    // Effect pour mettre à jour les champs first_name et last_name quand shippingType change
    // Seulement si aucune adresse n'est sélectionnée ET que les champs sont vides
    useEffect(() => {
        if (type === 'shipping' && !selectedAddressId && shippingType) {
            const firstName = shippingType === 'customer' ? customer?.firstname : therapist?.firstname
            const lastName = shippingType === 'customer' ? customer?.lastname : therapist?.lastname

            // Seulement mettre à jour si les champs sont vides pour éviter d'écraser les saisies utilisateur
            if (!formik.values[type]?.first_name) {
                formik.setFieldValue(`${type}.first_name`, firstName || "")
            }
            if (!formik.values[type]?.last_name) {
                formik.setFieldValue(`${type}.last_name`, lastName || "")
            }
        }
    }, [shippingType, customer, therapist, selectedAddressId, type, formik.values])


    return (
        <motion.div
            className="w-full overflow-visible"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            {/* Header avec titre et icône */}
            {title &&
                <motion.div
                    className="flex items-center gap-3 my-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                >
                    <MapPin className="h-5 w-5 text-primary" />
                    <h2 className="text-lg font-bold">{title}</h2>
                </motion.div>
            }

            <div className="space-y-6">
                {/* Section 1: Sélection d'adresse */}
                <div className="space-y-3">
                    <label className="flex flex-col gap-2">
                        <span className="text-sm font-medium">Sélectionnez une adresse</span>
                        <Field
                            name={`${type}AddressId`}
                            as="select"
                            className="select select-primary min-h-[44px]"
                            value={selectedAddressId || "new"}
                            onChange={handleAddressChange(type)}
                        >
                            <option value="new">Nouvelle Adresse</option>
                            {addresses.map((address) => (
                                <option key={address.id} value={address.id}>
                                    {address.street}, {address.city}
                                </option>
                            ))}
                        </Field>
                    </label>
                </div>

                {/* Section 2: Informations personnelles - masquée si hidePersonalInfo est true */}
                {!hidePersonalInfo && (
                    <motion.div
                        className="space-y-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isTransitioning ? 0.5 : 1 }}
                        transition={{ duration: 0.2 }}
                    >
                        {showSectionTitle && (
                            <div className="flex items-center gap-2 text-sm font-medium text-base-content/70">
                                <User className="h-4 w-4" />
                                <span>Informations personnelles</span>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Prénom */}
                            <div className="form-control">
                                <label className="block text-sm font-medium mb-2">
                                    Prénom <span className="text-error">*</span>
                                </label>
                                <Field
                                    name={`${type}.first_name`}
                                    placeholder="Prénom"
                                    value={formik.values[type]?.first_name || ''}
                                    onChange={handleFieldChange(`${type}.first_name`)}
                                    onBlur={handleFieldBlur(`${type}.first_name`)}
                                    className={`input input-primary min-h-[44px] ${errors?.[type]?.first_name ? 'input-error' : ''}`}
                                    aria-describedby={errors?.[type]?.first_name ? `${type}-first_name-error` : undefined}
                                />
                                <FieldError
                                    error={errors?.[type]?.first_name}
                                    fieldName={`${type}-first_name`}
                                />
                            </div>

                            {/* Nom */}
                            <div className="form-control">
                                <label className="block text-sm font-medium mb-2">
                                    Nom <span className="text-error">*</span>
                                </label>
                                <Field
                                    name={`${type}.last_name`}
                                    placeholder="Nom"
                                    value={formik.values[type]?.last_name || ''}
                                    onChange={handleFieldChange(`${type}.last_name`)}
                                    onBlur={handleFieldBlur(`${type}.last_name`)}
                                    className={`input input-primary min-h-[44px] ${errors?.[type]?.last_name ? 'input-error' : ''}`}
                                    aria-describedby={errors?.[type]?.last_name ? `${type}-last_name-error` : undefined}
                                />
                                <FieldError
                                    error={errors?.[type]?.last_name}
                                    fieldName={`${type}-last_name`}
                                />
                            </div>
                        </div>

                        {/* Champ Entreprise - pour facturation OU pour shipping si showCompanyField est true */}
                        <AnimatePresence>
                            {(type === 'billing' || (type === 'shipping' && showCompanyField)) && (
                                <motion.div
                                    className="form-control"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <label className="block text-sm font-medium mb-2">
                                        <span className="flex items-center gap-2">
                                            <Building2 className="h-4 w-4" />
                                            Entreprise (optionnel)
                                        </span>
                                    </label>
                                    <Field
                                        name={`${type}.company`}
                                        placeholder="Nom de l'entreprise"
                                        value={formik.values[type]?.company || ''}
                                        onChange={handleFieldChange(`${type}.company`)}
                                        onBlur={handleFieldBlur(`${type}.company`)}
                                        className="input input-primary min-h-[44px]"
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}

                {/* Section 3: Adresse physique */}
                <motion.div
                    className="space-y-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isTransitioning ? 0.5 : 1 }}
                    transition={{ duration: 0.2 }}
                >
                    {showSectionTitle && (
                        <div className="flex items-center gap-2 text-sm font-medium text-base-content/70">
                            <MapPin className="h-4 w-4" />
                            <span>Adresse physique</span>
                        </div>
                    )}

                    {/* Adresse */}
                    <div className="form-control">
                        <label className="block text-sm font-medium mb-2">
                            Adresse <span className="text-error">*</span>
                        </label>
                        <Field
                            name={`${type}.address_1`}
                            placeholder="Numéro et nom de rue"
                            value={formik.values[type]?.address_1 || ''}
                            onChange={handleFieldChange(`${type}.address_1`)}
                            onBlur={handleFieldBlur(`${type}.address_1`)}
                            className={`input input-primary min-h-[44px] ${errors?.[type]?.address_1 ? 'input-error' : ''}`}
                            aria-describedby={errors?.[type]?.address_1 ? `${type}-address_1-error` : undefined}
                        />
                        <FieldError
                            error={errors?.[type]?.address_1}
                            fieldName={`${type}-address_1`}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Ville */}
                        <div className="form-control">
                            <label className="block text-sm font-medium mb-2">
                                Ville <span className="text-error">*</span>
                            </label>
                            <Field
                                name={`${type}.city`}
                                placeholder="Ville"
                                value={formik.values[type]?.city || ''}
                                onChange={handleFieldChange(`${type}.city`)}
                                onBlur={handleFieldBlur(`${type}.city`)}
                                className={`input input-primary min-h-[44px] ${errors?.[type]?.city ? 'input-error' : ''}`}
                                aria-describedby={errors?.[type]?.city ? `${type}-city-error` : undefined}
                            />
                            <FieldError
                                error={errors?.[type]?.city}
                                fieldName={`${type}-city`}
                            />
                        </div>

                        {/* Code postal */}
                        <div className="form-control">
                            <label className="block text-sm font-medium mb-2">
                                Code postal <span className="text-error">*</span>
                            </label>
                            <Field
                                name={`${type}.postcode`}
                                placeholder="Code postal"
                                value={formik.values[type]?.postcode || ''}
                                onChange={handleFieldChange(`${type}.postcode`)}
                                onBlur={handleFieldBlur(`${type}.postcode`)}
                                className={`input input-primary min-h-[44px] ${errors?.[type]?.postcode ? 'input-error' : ''}`}
                                aria-describedby={errors?.[type]?.postcode ? `${type}-postcode-error` : undefined}
                            />
                            <FieldError
                                error={errors?.[type]?.postcode}
                                fieldName={`${type}-postcode`}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* État */}
                        <div className="form-control">
                            <label className="block text-sm font-medium mb-2">
                                État/Région
                            </label>
                            <Field
                                name={`${type}.state`}
                                placeholder="État ou région"
                                value={formik.values[type]?.state || ''}
                                onChange={handleFieldChange(`${type}.state`)}
                                onBlur={handleFieldBlur(`${type}.state`)}
                                className={`input input-primary min-h-[44px] ${errors?.[type]?.state ? 'input-error' : ''}`}
                                aria-describedby={errors?.[type]?.state ? `${type}-state-error` : undefined}
                            />
                            <FieldError
                                error={errors?.[type]?.state}
                                fieldName={`${type}-state`}
                            />
                        </div>

                        {/* Pays */}
                        <div className="form-control">
                            <label className="block text-sm font-medium mb-2">
                                Pays <span className="text-error">*</span>
                            </label>
                            <CountryInput
                                name={`${type}.country`}
                                placeholder="Sélectionner un pays"
                                onChange={handleFieldChange(`${type}.country`)}
                                onBlur={handleFieldBlur(`${type}.country`)}
                                className={`${errors?.[type]?.country ? 'border-error' : ''}`}
                                aria-describedby={errors?.[type]?.country ? `${type}-country-error` : undefined}
                            />
                            <FieldError
                                error={errors?.[type]?.country}
                                fieldName={`${type}-country`}
                            />
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
}

export default AddressForm;
